import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchBroadcastSettings, updateBroadcastSettings, type AdminBroadcastSettings } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/** A15.10 tezlik cheklovi (throttle) + A15.22 anti-spam siyosati. */
export function BroadcastSettings() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [s, setS] = useState<AdminBroadcastSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBroadcastSettings(ac.signal)
      .then((r) => {
        if (alive) setS(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const save = async () => {
    if (!s || busy) return
    setBusy(true)
    try {
      await updateBroadcastSettings(s)
      toast(t.broadcast.settingsSaved, { tone: 'success' })
    } catch (e) {
      toast(t.broadcast.createFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const numField = (label: string, hint: string, key: 'throttle_per_min' | 'daily_cap' | 'cooldown_hours') => (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-secondary">{label}</label>
      <input
        type="number"
        min={0}
        value={s ? s[key] : 0}
        onChange={(e) => s && setS({ ...s, [key]: Math.max(0, Number(e.target.value) || 0) })}
        className="w-40 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
      />
      <span className="text-xs text-tertiary">{hint}</span>
    </div>
  )

  return (
    <>
      <PageHeader
        title={t.broadcast.policy}
        screenId="A15.10"
        description={t.broadcast.policyNote}
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.broadcast.title}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-md space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} />
        ) : !s ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-md space-y-4 rounded-lg border border-border-default bg-surface-2 p-4">
            {numField(t.broadcast.throttle, t.broadcast.throttleHint, 'throttle_per_min')}
            {numField(t.broadcast.dailyCap, t.broadcast.dailyCapHint, 'daily_cap')}
            {numField(t.broadcast.cooldown, t.broadcast.cooldownHint, 'cooldown_hours')}
            <label className="flex items-center gap-2 text-sm text-primary">
              <input
                type="checkbox"
                checked={s.antispam_enabled}
                onChange={(e) => setS({ ...s, antispam_enabled: e.target.checked })}
              />
              {t.broadcast.antispam}
            </label>
            <button
              disabled={busy}
              onClick={save}
              className="rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
            >
              {t.common.save}
            </button>
          </div>
        )}
      </div>
    </>
  )
}
